interface LoadingScreenProps {
  /**
   * Optional message to display below the spinner.
   * If omitted, only the spinner will be shown for a minimal "Sovereign" feel.
   */
  message?: string;
  /**
   * The primary color of the business node.
   * Defaults to a neutral slate or the CSS variable if available.
   */
  color?: string | null;
  /**
   * Whether to occupy full screen or behave as inline loader. Defaults to true.
   */
  fullScreen?: boolean;
}

/**
 * LoadingScreen (V2.1.9)
 * Purpose: Unified, themed loading identity for the Hajez Sovereign platform.
 * Forensic Standard: 0-Leaked State / Highly Reusable.
 */
export function LoadingScreen({
  message,
  color,
  fullScreen = true,
}: LoadingScreenProps) {
  // Protocol: Respect the Business Primary Color or default to Sovereign Sapphire (#0F52BA)
  const primaryColor = color || "#0F52BA";

  return (
    <div
      className={
        fullScreen
          ? "min-h-[100dvh] flex flex-col items-center justify-center bg-gray-50/10 backdrop-blur-[2px] fixed inset-0 z-[100] animate-in fade-in duration-500"
          : "w-full py-12 flex flex-col items-center justify-center animate-in fade-in duration-500"
      }
    >
      <div className="relative flex flex-col items-center">
        {/* Themed Spinner: Clean, High-Contrast */}
        <div
          className={fullScreen ? "relative w-16 h-16" : "relative w-10 h-10"}
        >
          <div className="absolute inset-0 rounded-full border-[3px] border-slate-100/50" />
          <div
            className="absolute inset-0 rounded-full border-[3px] border-t-transparent animate-spin"
            style={{
              borderRightColor: primaryColor,
              borderBottomColor: primaryColor,
              borderLeftColor: primaryColor,
              borderTopColor: "transparent",
            }}
          />
        </div>

        {/* Optional Forensic Message */}
        {message && (
          <p
            className={`${fullScreen ? "mt-6 text-[10px]" : "mt-3 text-[9px]"} font-black uppercase tracking-[0.3em] text-slate-400 animate-pulse`}
          >
            {message}
          </p>
        )}
      </div>
    </div>
  );
}
